package com.schoolconnect.app.feature.classconversations

import com.schoolconnect.app.datasource.network.response.ClassConversationListResponse
import com.schoolconnect.app.datasource.network.response.ClassConversationReplyResponse
import com.schoolconnect.app.datasource.network.response.ClassConversationResponse
import com.schoolconnect.app.feature.common.PaginationPolicy

data class ConversationClassSectionOption(val id: String, val label: String)

const val CONVERSATION_PERIOD_ALL = "All Time"
const val CONVERSATION_PERIOD_TODAY = "Today"
const val CONVERSATION_PERIOD_LAST_7_DAYS = "Last 7 Days"
const val CONVERSATION_PERIOD_THIS_MONTH = "This Month"

val conversationPeriodOptions = listOf(
    CONVERSATION_PERIOD_ALL,
    CONVERSATION_PERIOD_TODAY,
    CONVERSATION_PERIOD_LAST_7_DAYS,
    CONVERSATION_PERIOD_THIS_MONTH
)

data class ClassConversationsUiState(
    val rows: List<ClassConversationListResponse> = emptyList(),
    val totalCount: Int = 0,
    val pageIndex: Int = 0,
    val pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
    val sortField: ClassConversationsSortField = ClassConversationsSortField.POSTED_ON,
    val sortAscending: Boolean = false,
    val query: String = "",
    val classSectionId: String = "",
    val classSectionOptions: List<ConversationClassSectionOption> = emptyList(),
    val period: String = CONVERSATION_PERIOD_ALL,
    val periodFrom: String = "",
    val periodTo: String = "",
    val isClassSectionLoading: Boolean = false,
    val filterLookupError: String = "",
    val isLoading: Boolean = false,
    val errorMessage: String = "",
    val selected: ClassConversationResponse? = null,
    val replies: List<ClassConversationReplyResponse> = emptyList(),
    val isDetailLoading: Boolean = false,
    val detailErrorMessage: String = "",
    val isMutating: Boolean = false,
    val mutationErrorMessage: String = "",
    val replyResetGeneration: Int = 0
)

enum class ClassConversationsSortField {
    ID, TITLE, CLASS, POSTED_BY, POSTED_ON, REPLIES, STATUS
}

fun ClassConversationsSortField.toApiSortField(): String = when (this) {
    ClassConversationsSortField.ID -> "conversation_code"
    ClassConversationsSortField.TITLE -> "title"
    ClassConversationsSortField.CLASS -> "class_name"
    ClassConversationsSortField.POSTED_BY -> "author_name"
    ClassConversationsSortField.POSTED_ON -> "created_at"
    ClassConversationsSortField.REPLIES -> "reply_count"
    ClassConversationsSortField.STATUS -> "status"
}

sealed interface ClassConversationsUiEvent {
    data object Refresh : ClassConversationsUiEvent
    data class QueryChanged(val value: String) : ClassConversationsUiEvent
    data object FiltersReset : ClassConversationsUiEvent
    data class ClassSectionChanged(val classSectionId: String) : ClassConversationsUiEvent
    data class PeriodChanged(val value: String) : ClassConversationsUiEvent
    data class CustomPeriodChanged(val from: String, val to: String) : ClassConversationsUiEvent
    data class PageChanged(val pageIndex: Int) : ClassConversationsUiEvent
    data class PageSizeChanged(val pageSize: Int) : ClassConversationsUiEvent
    data class SortChanged(val field: ClassConversationsSortField) : ClassConversationsUiEvent
    data class ViewRequested(val conversationId: String) : ClassConversationsUiEvent
    data object ViewDismissed : ClassConversationsUiEvent
    data class ReplyRequested(val conversationId: String, val replyText: String, val attachmentUrl: String? = null, val attachmentFilename: String? = null) : ClassConversationsUiEvent
    data class CreateRequested(val title: String, val description: String, val attachmentUrl: String? = null, val attachmentFilename: String? = null, val attachmentSizeBytes: Int? = null) : ClassConversationsUiEvent
    data class UpdateRequested(val conversationId: String, val title: String, val description: String, val attachmentUrl: String? = null, val attachmentFilename: String? = null, val attachmentSizeBytes: Int? = null, val attachmentChanged: Boolean = false) : ClassConversationsUiEvent
    data class ModerateRequested(val conversationId: String, val action: String, val reason: String? = null) : ClassConversationsUiEvent
    data class DeleteRequested(val conversationId: String, val reason: String? = null) : ClassConversationsUiEvent
    data class DeleteReplyRequested(val conversationId: String, val replyId: String, val reason: String? = null) : ClassConversationsUiEvent
}
